
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { LockIcon, Mail } from 'lucide-react';
import AuthHeader from '@/components/auth/AuthHeader';
import LoginForm from '@/components/auth/LoginForm';
import SignupForm from '@/components/auth/SignupForm';
import AdminForm from '@/components/auth/AdminForm';
import AuthFooter from '@/components/auth/AuthFooter';
import EmailVerificationHandler from '@/components/auth/EmailVerificationHandler';
import PasswordResetForm from '@/components/auth/PasswordResetForm';
import { sanitizeRedirectPath } from '@/utils/redirectUtils';

type FormData = {
  email: string;
  password: string;
};

const Auth = () => {
  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'admin'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if this is an email verification flow
  const hasVerificationParams = searchParams.get('token') || 
                                searchParams.get('token_hash') || 
                                searchParams.get('verified') === 'true';

  // Check if this is a password reset flow
  const isPasswordReset = searchParams.get('reset') === 'true' || 
                          (searchParams.get('type') === 'recovery' && searchParams.get('token'));

  // If we have verification parameters, show only the verification handler
  if (hasVerificationParams && !isPasswordReset) {
    return (
      <div className="container mx-auto max-w-md p-4">
        <Card>
          <CardContent className="p-6">
            <EmailVerificationHandler />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show password reset form if this is a password reset flow (regardless of login status)
  if (isPasswordReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="w-full max-w-md">
          <Card className="shadow-xl border-0">
            <CardContent className="p-6">
              <PasswordResetForm />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" />;
  }

  const handleLogin = async (data: FormData) => {
    setLoading(true);
    setError(null);
    const { error } = await signIn(data.email, data.password);
    
    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      // Check if they were in the middle of a subscription signup
      const redirectPath = localStorage.getItem('postAuthRedirect') || 
                          sessionStorage.getItem('postAuthRedirect');
      localStorage.removeItem('postAuthRedirect');
      sessionStorage.removeItem('postAuthRedirect');
      const safePath = sanitizeRedirectPath(redirectPath);
      navigate(safePath);
    }
  };

  const handleAdminLogin = async (data: FormData) => {
    setLoading(true);
    setError(null);
    try {
      const { error } = await signIn(data.email, data.password);
      
      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        toast.success('Admin login successful!');
        navigate('/subscriptions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setLoading(false);
    }
  };

  const handleSignup = async (data: FormData) => {
    setLoading(true);
    setError(null);
    
    // Basic validation
    if (!data.email || !data.password) {
      setError("Please fill in all fields.");
      setLoading(false);
      return;
    }
    
    if (data.password.length < 6) {
      setError("Password must be at least 6 characters long.");
      setLoading(false);
      return;
    }
    
    const { error } = await signUp(data.email, data.password);
    
    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      setError(null);
      setLoading(false);
      // Show success message and switch to login tab
      toast.success("Account created! Please check your email (including your junk folder - don't let verification emails get binned!) for a verification link.");
      setAuthMode('login');
    }
  };

  const handleModeChange = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-0">
          <AuthHeader authMode={authMode} />
          <CardContent className="p-6">
            {/* Temporary email verification notice */}
            <Alert className="mb-6 border-blue-200 bg-blue-50 text-blue-800">
              <Mail className="h-4 w-4" />
              <AlertDescription>
                Having trouble verifying your email? We're on it! Email admin@binsandthings.co.uk for help.
              </AlertDescription>
            </Alert>
            
            <Tabs value={authMode} onValueChange={(value) => setAuthMode(value as 'login' | 'signup' | 'admin')}>
              <TabsList className="grid w-full grid-cols-3 h-12 mb-6">
                <TabsTrigger value="login" className="text-sm font-medium">Login</TabsTrigger>
                <TabsTrigger value="signup" className="text-sm font-medium">Sign Up</TabsTrigger>
                <TabsTrigger value="admin" className="bg-slate-100 text-sm font-medium">
                  <LockIcon className="h-4 w-4 mr-1" />
                  Admin
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="login" className="space-y-0">
                <LoginForm 
                  onSubmit={handleLogin}
                  loading={loading}
                  error={error}
                />
              </TabsContent>
              
              <TabsContent value="signup" className="space-y-0">
                <SignupForm 
                  onSubmit={handleSignup}
                  loading={loading}
                  error={error}
                />
              </TabsContent>

              <TabsContent value="admin" className="space-y-0">
                <AdminForm 
                  onSubmit={handleAdminLogin}
                  loading={loading}
                  error={error}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
          <AuthFooter authMode={authMode} onModeChange={handleModeChange} />
        </Card>
      </div>
    </div>
  );
};

export default Auth;
